import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AppApis } from '../../shared/app.apis';
import { AppConstants } from '../../shared/app.constants';
import { AppApi } from '../../shared/app.models';
import { LoaderService } from '../services/loader.service';
import { TenantService } from '../services/tenant-service';

const API_CONFIG = Object.values(AppApis);

function matchesApi(api: AppApi, method: string, path: string): boolean {
  if (api.method.toUpperCase() !== method.toUpperCase()) {
    return false;
  }

  if (api.pattern) {
    return api.pattern.test(path);
  }

  if (typeof api.url === 'string') {
    return api.url === path;
  }

  return false;
}

function getApiConfig(method: string, path: string): AppApi | undefined {
  return API_CONFIG.find((api) => matchesApi(api, method, path));
}

export const apiRequestInterceptor: HttpInterceptorFn = (req, next) => {
  const tenantService = inject(TenantService);
  const loaderService = inject(LoaderService);

  const baseUrl = environment.apiUrl;
  const isAbsoluteUrl = /^https?:\/\//i.test(req.url);
  const isBlobStorage = /^https?:\/\/.*\.blob\.core\.windows\.net/i.test(req.url);

  if (isBlobStorage) {
    return next(req);
  }

  const relativePath = req.url.startsWith('/') ? req.url : `/${req.url}`;
  const api = getApiConfig(req.method, relativePath);

  const token = localStorage.getItem(AppConstants.USER_TOKEN_KEY);

  if (!api?.public && !token) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized',
          error: {
            success: false,
            message: 'Authentication token is missing.',
          },
        }),
    );
  }

  let params = req.params;

  const filter = params.get('f');

  if (filter) {
    params = params.set('f', toBase64(filter));
  }

  let headers = req.headers
    .set('X-Device-Id', getDeviceId())
    .set('X-Device-Name', getDeviceName())
    .set('X-App-Version', environment.version)
    .set('X-Platform', getPlatform())
    .set('X-O-Code', tenantService.retrieveClientCode())
    .set('X-Platform-Version', getPlatformVersion())
    .set('X-Request-Time', new Date().toISOString());

  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const apiReq = req.clone({
    url: isAbsoluteUrl ? req.url : `${baseUrl}${relativePath}`,
    headers,
    params,
    withCredentials: true,
  });

  if (api?.noLoader) {
    return next(apiReq);
  }

  loaderService.show();

  return next(apiReq).pipe(
    finalize(() => {
      loaderService.hide();
    }),
  );
};

function getPlatform(): string {
  const ua = navigator.userAgent;

  if (/android/i.test(ua)) {
    return 'Android';
  }

  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'iOS';
  }

  return 'Web';
}

function getPlatformVersion(): string {
  return navigator.userAgent;
}

function getDeviceName(): string {
  return navigator.userAgent;
}

function getDeviceId(): string {
  const key = 'device-id';

  let deviceId = localStorage.getItem(key);

  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(key, deviceId);
  }

  return deviceId;
}

function toBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const binString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');

  return btoa(binString);
}
